<script setup lang="ts">
import { inject, ref } from 'vue';
import type { StepCategory } from 'src/types/course/step';
import { STEP_QUESTION_CONCEALED } from './stepCardKeys';

interface Props {
  stepCategory?: StepCategory;
}
withDefaults(defineProps<Props>(), {
  stepCategory: 'Practice',
});

const isConcealed = inject(STEP_QUESTION_CONCEALED, ref(false));
</script>
<template>
  <q-card class="drill-card">
    <slot name="progress"></slot>
    <slot name="header"></slot>
    <slot></slot>

    <q-card-section class="q-py-none">
      <slot name="error"> </slot>
    </q-card-section>

    <!-- The question and the answer area can't be selected or copied: pasted
         into a translator they give the answer away. -->
    <q-card-section
      v-if="stepCategory !== 'Theory'"
      class="q-py-none relative-position step-card--no-copy"
      :class="{ 'step-card--concealed': isConcealed }"
      @copy.prevent
      @cut.prevent
      @contextmenu.prevent
    >
      <div class="absolute-right z-100">
        <slot name="tts"></slot>
      </div>
      <slot name="drill"> </slot>
    </q-card-section>

    <q-card-section
      v-if="stepCategory !== 'Theory'"
      class="scroll q-pr-none step-card--no-copy"
      :class="{ 'step-card--concealed': isConcealed }"
      @copy.prevent
      @cut.prevent
      @contextmenu.prevent
    >
      <slot name="solve"> </slot>
    </q-card-section>

    <q-space />
    <q-card-section class="q-pa-none">
      <slot name="footer"></slot>
    </q-card-section>
  </q-card>
</template>

<style scoped>
/* In dark mode Quasar paints a card one shade lighter than the page, so the
   drill showed as a lighter box in a darker frame. Light mode is white on
   white; match it. */
:global(.body--dark .drill-card) {
  background: transparent;
  box-shadow: none;
}
.step-card--concealed {
  visibility: hidden;
}
.step-card--no-copy {
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
/* Typing answers (and Fill in the Blank, inside the question) stay usable: iOS
   Safari can refuse input in a no-select container. */
.step-card--no-copy :deep(input),
.step-card--no-copy :deep(textarea) {
  user-select: text;
  -webkit-user-select: text;
}
</style>
